import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { timezoneService } from '../services/timezoneService';
import { useUser } from '../contexts/UserContext';
import AttachmentList from '../components/AttachmentList';
import FileUpload from '../components/FileUpload';
import { TicketDescriptionContent } from '../components/TicketDescriptionContent';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formattedDates, setFormattedDates] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
     const [responses, setResponses] = useState([]);
   const [loadingResponses, setLoadingResponses] = useState(false);
   const [responsesCollapsed, setResponsesCollapsed] = useState(true);
   const [updateForm, setUpdateForm] = useState({
    status: '',
    priority: '',
    categoryId: '',
    requestTypeId: '',
    response: '',
    internalResponse: '',
    rootCause: '',
    solution: '',
    technicianId: ''
  });
  const [activeResponseTab, setActiveResponseTab] = useState('internal'); // 'internal' only
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('rootCause'); // 'rootCause' or 'solution'
  const [userComment, setUserComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [projectTrackerEnabled, setProjectTrackerEnabled] = useState(false);
  const [linkedProject, setLinkedProject] = useState(null);
  const [showLinkProject, setShowLinkProject] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectSearchResults, setProjectSearchResults] = useState([]);
  const [searchingProjects, setSearchingProjects] = useState(false);
  const [linkingProject, setLinkingProject] = useState(false);

  const loadAttachments = async () => {
    try {
      const attachmentsData = await apiService.getAttachmentsByTicket(id);
      setAttachments(attachmentsData);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      // Don't set error for attachments, as it's not critical
    }
  };

  const loadResponses = async () => {
    try {
      setLoadingResponses(true);
      // Pass user role to filter internal responses appropriately
      const userRole = isAdminOrTechnician() ? (isTechnician() ? 'Technician' : 'Admin') : 'User';
      const responsesData = await apiService.getTicketResponses(id, userRole);
      setResponses(responsesData);
    } catch (err) {
      console.error('Failed to load responses:', err);
      // Don't set error for responses, as it's not critical
    } finally {
      setLoadingResponses(false);
    }
  };

  useEffect(() => {
    const loadTicket = async () => {
      try {
        setLoading(true);
        setError(null);
        const ticketData = await apiService.getTicketById(id);
        setTicket(ticketData);
        
        // Format dates with timezone
        const formatted = {
          createdAt: await timezoneService.formatDateTime(ticketData.createdAt),
          updatedAt: await timezoneService.formatDateTime(ticketData.updatedAt),
          slaTarget: ticketData.slaTarget ? await timezoneService.formatDateTime(ticketData.slaTarget) : 'N/A',
          slaResolved: ticketData.slaResolved ? await timezoneService.formatDateTime(ticketData.slaResolved) : 'N/A'
        };
        setFormattedDates(formatted);
        
        // Initialize update form with current values (response fields start empty)
        setUpdateForm({
          status: ticketData.status.toString(),
          priority: ticketData.priority.toString(),
          categoryId: ticketData.categoryId.toString(),
          requestTypeId: ticketData.requestTypeId ? ticketData.requestTypeId.toString() : '',
          response: '', // Start with empty response
          internalResponse: '', // Start with empty internal response
          rootCause: '', // Start with empty root cause
          solution: '', // Start with empty solution
          technicianId: ticketData.technicianId || ''
        });
      } catch (err) {
        console.error('Failed to load ticket:', err);
        setError('Failed to load ticket details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const loadTechnicians = async () => {
      try {
        const usersData = await apiService.getUsers();
        // Filter only technicians (role 2) and admins (role 3)
        const techUsers = usersData.filter(user => user.role === 2 || user.role === 3);
        setTechnicians(techUsers);
      } catch (err) {
        console.error('Failed to load technicians:', err);
        // Don't set error for technicians, as it's not critical
      }
    };

    const loadCategories = async () => {
      try {
        const categoriesData = await apiService.getCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load categories:', err);
        // Don't set error for categories, as it's not critical
      }
    };

    const loadProjectTracker = async () => {
      try {
        const enabled = await apiService.isProjectTrackerEnabled();
        setProjectTrackerEnabled(!!enabled);
        if (enabled) {
          const proj = await apiService.getTicketProject(id);
          setLinkedProject(proj || null);
        }
      } catch {
        // not critical
      }
    };

    loadTicket();
    loadTechnicians();
    loadCategories();
    loadAttachments();
    loadResponses();
    loadProjectTracker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSearchProjects = async () => {
    if (!projectSearch.trim()) return;
    setSearchingProjects(true);
    try {
      const results = await apiService.getProjects();
      const q = projectSearch.toLowerCase();
      const filtered = (Array.isArray(results) ? results : [])
        .filter(p => p.name?.toLowerCase().includes(q) || String(p.id).includes(q))
        .slice(0, 8);
      setProjectSearchResults(filtered);
    } catch {
      alert('Failed to search projects');
    } finally {
      setSearchingProjects(false);
    }
  };

  const handleLinkProject = async (projectId) => {
    setLinkingProject(true);
    try {
      await apiService.linkTicketToProject(id, projectId);
      const proj = await apiService.getTicketProject(id);
      setLinkedProject(proj || null);
      setShowLinkProject(false);
      setProjectSearch('');
      setProjectSearchResults([]);
    } catch (err) {
      const is409 = err?.message?.includes('409') || err?.status === 409;
      alert(is409 ? 'This ticket is already linked to a project.' : 'Failed to link project.');
    } finally {
      setLinkingProject(false);
    }
  };

  const handleUnlinkProject = async () => {
    if (!window.confirm('Unlink this ticket from the project?')) return;
    try {
      await apiService.unlinkTicketFromProjectByTicket(id);
      setLinkedProject(null);
    } catch {
      alert('Failed to unlink ticket from project');
    }
  };

  const getStatusIcon = (status) => {
    // Handle case where status might be an object
    const statusValue = typeof status === 'object' ? status?.id || status?.value || 1 : status;
    switch (statusValue) {
      case 1: return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 2: return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 3: return <ClockIcon className="h-4 w-4 text-orange-500" />;
      case 4: return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 5: return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    // Handle case where status might be an object
    const statusValue = typeof status === 'object' ? status?.id || status?.value || 1 : status;
    switch (statusValue) {
      case 1: return 'New';
      case 2: return 'In Progress';
      case 3: return 'Waiting Feedback';
      case 4: return 'Closed';
      case 5: return 'Reopened';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    // Handle case where priority might be an object
    const priorityValue = typeof priority === 'object' ? priority?.id || priority?.value || 2 : priority;
    switch (priorityValue) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority) => {
    // Handle case where priority might be an object
    const priorityValue = typeof priority === 'object' ? priority?.id || priority?.value || 2 : priority;
    switch (priorityValue) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  };

  const handleSubmitTechnicianResponse = async (e) => {
    e.preventDefault();

    const updateCategory = categories.find(c => c.id.toString() === updateForm.categoryId);
    const updateRequestTypes = updateCategory?.requestTypes || [];
    if (updateRequestTypes.length > 0 && !updateForm.requestTypeId) {
      alert('Please select a request type for this category');
      return;
    }

    try {
      setUpdating(true);

      // Find the selected technician's name
      const selectedTechnician = technicians.find(tech => tech.id === updateForm.technicianId);

      // Create a response entry instead of updating the ticket directly
      const responseData = {
        Status: parseInt(updateForm.status),
        Priority: parseInt(updateForm.priority),
        CategoryId: parseInt(updateForm.categoryId),
        RequestTypeId: updateForm.requestTypeId ? parseInt(updateForm.requestTypeId) : null,
        Response: updateForm.response.trim() || null,
        InternalResponse: updateForm.internalResponse.trim() || null,
        RootCause: updateForm.rootCause.trim() || null,
        Solution: updateForm.solution.trim() || null,
        TechnicianId: updateForm.technicianId || null,
        TechnicianName: selectedTechnician?.name || null,
        ResponseType: 'Update'
      };

      // Create the response (this will also update the main ticket)
      await apiService.createTicketResponse(id, responseData);
      
      // Reload the ticket and responses
      const updatedTicket = await apiService.getTicketById(id);
      setTicket(updatedTicket);
      await loadResponses();
      
      // Clear the form with the updated ticket values
      setUpdateForm({
        status: updatedTicket.status.toString(),
        priority: updatedTicket.priority.toString(),
        categoryId: updatedTicket.categoryId.toString(),
        requestTypeId: updatedTicket.requestTypeId ? updatedTicket.requestTypeId.toString() : '',
        response: '',
        internalResponse: '',
        rootCause: '',
        solution: '',
        technicianId: updatedTicket.technicianId || ''
      });
      
      // Response added successfully - no popup needed
    } catch (err) {
      console.error('Failed to add response:', err);
      alert('Failed to add response. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'categoryId' ? { requestTypeId: '' } : {})
    }));
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const trimmed = userComment.trim();
    if (!trimmed) return;
    try {
      setSubmittingComment(true);
      await apiService.createTicketResponse(id, {
        Response: trimmed,
        ResponseType: 'Comment'
      });
      setUserComment('');
      const updatedTicket = await apiService.getTicketById(id);
      setTicket(updatedTicket);
      await loadResponses();
    } catch (err) {
      console.error('Failed to add comment:', err);
      const message = err?.message || err?.response?.message || 'Failed to add comment. Please try again.';
      alert(typeof message === 'string' ? message : 'Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFiles(prev => [...prev, file]);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAttachments = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploadingFiles(true);
      for (const file of selectedFiles) {
        await apiService.uploadAttachment(id, file);
      }
      setSelectedFiles([]);
      await loadAttachments(); // Reload attachments
      // Attachments uploaded successfully - no popup needed
    } catch (error) {
      console.error('Failed to upload attachments:', error);
      alert('Failed to upload attachments. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleAttachmentDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteAttachment(attachmentId);
      setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      alert('Attachment deleted successfully!');
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      alert('Failed to delete attachment. Please try again.');
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await apiService.deleteTicket(id);
      alert('Ticket deleted successfully!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to delete ticket:', err);
      alert('Failed to delete ticket. Please try again.');
    } finally {
      setDeleting(false);
    }
  };



  // Get user role from context for proper access control
  const { isTechnician, isAdminOrTechnician } = useUser();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Ticket not found</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
             <div className="flex items-center justify-between">
         <div>
           <button
             onClick={() => navigate('/dashboard')}
             className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
           >
             <ArrowLeftIcon className="h-4 w-4 mr-1" />
             Back to Dashboard
           </button>
           <h1 className="text-2xl font-bold text-gray-900">Ticket #{ticket.id}</h1>
           {ticket.workItemUrl && (
             <a
               href={ticket.workItemUrl}
               target="_blank"
               rel="noopener noreferrer"
               className="mt-2 inline-flex items-center text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-1 hover:bg-blue-100"
             >
               <LinkIcon className="h-4 w-4 mr-1" />
               Azure DevOps work item #{ticket.externalRef}
             </a>
           )}
         </div>
         
         {isAdminOrTechnician() && (
           <button
             onClick={handleDeleteTicket}
             disabled={deleting}
             className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
           >
             {deleting ? 'Deleting...' : 'Delete Ticket'}
           </button>
         )}
       </div>

             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         {/* Main Content */}
         <div className="lg:col-span-2 space-y-6">
           {/* Ticket Details */}
           <div className="bg-white shadow rounded-lg p-6">
             <div className="flex items-start justify-between mb-4">
               <h2 className="text-xl font-semibold text-gray-900">{ticket.title}</h2>
               <div className="flex items-center space-x-2">
                 {getStatusIcon(ticket.status)}
                 <span className="text-sm font-medium text-gray-900">
                   {getStatusText(ticket.status)}
                 </span>
                 {ticket.isSlaBreached && (
                   <XCircleIcon className="h-4 w-4 text-red-500" />
                 )}
               </div>
             </div>

             <div className="prose max-w-none">
               <TicketDescriptionContent description={ticket.description} />
             </div>
           </div>

           {/* Add comment - for ticket owner (regular users) */}
           {!isAdminOrTechnician() && (
             <div className="bg-white shadow rounded-lg p-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Add a comment</h3>
               <p className="text-sm text-gray-500 mb-3">
                 Add more information or reply to updates on this ticket. Technicians will see your comment in the response history.
               </p>
               <form onSubmit={handleAddComment} className="space-y-3">
                 <textarea
                   value={userComment}
                   onChange={(e) => setUserComment(e.target.value)}
                   rows={3}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                   placeholder="Type your comment..."
                   disabled={submittingComment}
                 />
                 <div className="flex justify-end">
                   <button
                     type="submit"
                     disabled={submittingComment || !userComment.trim()}
                     className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                   >
                     {submittingComment ? 'Adding...' : 'Add comment'}
                   </button>
                 </div>
               </form>
             </div>
           )}

           {/* Technician/Admin Update Form - Moved to top */}
           {isAdminOrTechnician() && (
                           <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Details</h3>
               <form onSubmit={handleSubmitTechnicianResponse} className="space-y-4">
                                   <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={updateForm.status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="1">New</option>
                        <option value="2">In Progress</option>
                        <option value="3">Waiting Feedback</option>
                        <option value="4">Closed</option>
                        <option value="5">Reopened</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        id="priority"
                        name="priority"
                        value={updateForm.priority}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="1">Low</option>
                        <option value="2">Medium</option>
                        <option value="3">High</option>
                        <option value="4">Critical</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        id="categoryId"
                        name="categoryId"
                        value={updateForm.categoryId}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {(categories.find(c => c.id.toString() === updateForm.categoryId)?.requestTypes?.length > 0) && (
                      <div>
                        <label htmlFor="requestTypeId" className="block text-sm font-medium text-gray-700 mb-2">
                          Request Type
                        </label>
                        <select
                          id="requestTypeId"
                          name="requestTypeId"
                          value={updateForm.requestTypeId}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a request type</option>
                          {categories.find(c => c.id.toString() === updateForm.categoryId).requestTypes.map((requestType) => (
                            <option key={requestType.id} value={requestType.id}>
                              {requestType.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                 <div>
                   <label htmlFor="technicianId" className="block text-sm font-medium text-gray-700 mb-2">
                     Assign Technician
                   </label>
                   <select
                     id="technicianId"
                     name="technicianId"
                     value={updateForm.technicianId}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                   >
                     <option value="">No Technician Assigned</option>
                     {technicians.map((technician) => (
                       <option key={technician.id} value={technician.id}>
                         {technician.name} ({technician.role === 2 ? 'Technician' : 'Admin'})
                       </option>
                     ))}
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     Response
                   </label>
                   
                   {/* Response Tabs */}
                   <div className="flex border-b border-gray-200 mb-4">
                     {isAdminOrTechnician() && (
                       <button
                         type="button"
                         onClick={() => setActiveResponseTab('internal')}
                         className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                           activeResponseTab === 'internal'
                             ? 'border-blue-500 text-blue-600'
                             : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                         }`}
                       >
                         Internal Response
                         <span className="ml-1 text-xs text-orange-600">(Tech & Admin Only)</span>
                       </button>
                     )}
                   </div>
                   
                   {/* Tab Content */}
                   <div className="relative">
                     {activeResponseTab === 'internal' && (
                       <textarea
                         id="internalResponse"
                         name="internalResponse"
                         rows={3}
                         value={updateForm.internalResponse}
                         onChange={handleInputChange}
                         className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                         placeholder="Add internal notes for technicians and admins only..."
                       />
                     )}
                   </div>
                 </div>

                 <div>
                   <label htmlFor="response" className="block text-sm font-medium text-gray-700 mb-2">
                     Response to User
                   </label>
                   <textarea
                     id="response"
                     name="response"
                     rows={3}
                     value={updateForm.response}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                     placeholder="Provide a response to the user..."
                   />
                 </div>

                                   <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Analysis
                    </label>
                    
                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200 mb-2">
                      <button
                        type="button"
                        onClick={() => setActiveAnalysisTab('rootCause')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          activeAnalysisTab === 'rootCause'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Root Cause
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveAnalysisTab('solution')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          activeAnalysisTab === 'solution'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Solution
                      </button>
                    </div>
                    
                    {/* Tab Content */}
                    <div className="relative">
                      {activeAnalysisTab === 'rootCause' && (
                        <textarea
                          id="rootCause"
                          name="rootCause"
                          rows={3}
                          value={updateForm.rootCause}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Identify the root cause..."
                        />
                      )}
                      
                      {activeAnalysisTab === 'solution' && (
                        <textarea
                          id="solution"
                          name="solution"
                          rows={3}
                          value={updateForm.solution}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Describe the solution implemented..."
                        />
                      )}
                    </div>
                  </div>

                 <div className="flex justify-end">
                   <button
                     type="submit"
                     disabled={updating}
                     className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                   >
                                           {updating ? 'Updating...' : 'Update'}
                   </button>
                 </div>
               </form>
             </div>
           )}

           {/* Add New Attachments - Moved to second position */}
           {isAdminOrTechnician() && (
             <div className="bg-white shadow rounded-lg p-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Attachments</h3>
               <FileUpload
                 onFileSelect={handleFileSelect}
                 onFileRemove={handleFileRemove}
                 selectedFiles={selectedFiles}
                 maxFiles={5}
                 maxSizeMB={10}
               />
               {selectedFiles.length > 0 && (
                 <div className="mt-4">
                   <button
                     type="button"
                     onClick={handleUploadAttachments}
                     disabled={uploadingFiles}
                     className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                   >
                     {uploadingFiles ? 'Uploading...' : 'Upload Attachments'}
                   </button>
                 </div>
               )}
             </div>
           )}

           {/* Attachments Section */}
           <div className="bg-white shadow rounded-lg p-6">
             <AttachmentList 
               attachments={attachments} 
               onAttachmentDelete={handleAttachmentDelete}
               canDelete={isAdminOrTechnician()}
             />
           </div>

           {/* Responses History - Made collapsible */}
           <div className="bg-white shadow rounded-lg p-6">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-medium text-gray-900">Response History</h3>
               <button
                 onClick={() => setResponsesCollapsed(!responsesCollapsed)}
                 className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
               >
                 {responsesCollapsed ? 'Show' : 'Hide'} History
                 <svg
                   className={`ml-1 h-4 w-4 transform transition-transform ${responsesCollapsed ? 'rotate-0' : 'rotate-180'}`}
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24"
                 >
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                 </svg>
               </button>
             </div>
             
             {!responsesCollapsed && (
               <>
                 {loadingResponses ? (
                   <div className="flex items-center justify-center py-4">
                     <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                   </div>
                 ) : responses.length === 0 ? (
                   <p className="text-sm text-gray-500 italic">No responses yet</p>
                 ) : (
                   <div className="space-y-4">
                     {responses.map((response) => (
                       <div key={response.id} className="border-l-4 border-blue-500 pl-4 py-2">
                         <div className="flex items-center justify-between mb-2">
                           <div className="flex items-center space-x-2">
                             <span className="text-sm font-medium text-gray-900">{response.userName}</span>
                             <span className="text-xs text-gray-500">•</span>
                             <span className="text-xs text-gray-500">
                               {new Date(response.createdAt).toLocaleString()}
                             </span>
                           </div>
                           <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                             {response.responseType}
                           </span>
                         </div>
                         
                         {response.response && (
                           <div className="mb-2">
                             <span className="text-xs font-medium text-gray-500">Response:</span>
                             <p className="text-sm text-gray-900 mt-1">{response.response}</p>
                           </div>
                         )}
                         
                         {response.internalResponse && isAdminOrTechnician() && (
                           <div className="mb-2">
                             <span className="text-xs font-medium text-gray-500 text-orange-600">Internal Response (Technicians & Admins Only):</span>
                             <p className="text-sm text-gray-900 mt-1 bg-orange-50 p-2 rounded border-l-2 border-orange-300">{response.internalResponse}</p>
                           </div>
                         )}
                         
                         
                         
                         {response.rootCause && (
                           <div className="mb-2">
                             <span className="text-xs font-medium text-gray-500">Root Cause:</span>
                             <p className="text-sm text-gray-900 mt-1">{response.rootCause}</p>
                           </div>
                         )}
                         
                         {response.solution && (
                           <div className="mb-2">
                             <span className="text-xs font-medium text-gray-500">Solution:</span>
                             <p className="text-sm text-gray-900 mt-1">{response.solution}</p>
                           </div>
                         )}
                         
                         {(response.status || response.priority || response.categoryId || response.requestTypeId || response.technicianName) && (
                           <div className="text-xs text-gray-500 space-y-1">
                             {response.status && (
                               <div>Status changed to: <span className="font-medium">{getStatusText(response.status)}</span></div>
                             )}
                             {response.priority && (
                               <div>Priority changed to: <span className="font-medium">{getPriorityText(response.priority)}</span></div>
                             )}
                             {response.categoryId && (
                               <div>Category changed to: <span className="font-medium">{categories.find(c => c.id === response.categoryId)?.name || 'Unknown'}</span></div>
                             )}
                             {response.requestTypeId && (
                               <div>Request type changed to: <span className="font-medium">{categories.flatMap(c => c.requestTypes || []).find(rt => rt.id === response.requestTypeId)?.name || 'Unknown'}</span></div>
                             )}
                             {response.technicianName && (
                               <div>Assigned to: <span className="font-medium">{response.technicianName}</span></div>
                             )}
                           </div>
                         )}
                       </div>
                     ))}
                   </div>
                 )}
               </>
             )}
           </div>


         </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Ticket Information</h3>
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Category</span>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-900">{ticket.categoryName}</p>
                </div>
              </div>
              {ticket.requestTypeName && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Request Type</span>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-900">{ticket.requestTypeName}</p>
                  </div>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">Priority</span>
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                    {getPriorityText(ticket.priority)}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Created</span>
                <p className="text-sm text-gray-900">{formattedDates.createdAt}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Last Updated</span>
                <p className="text-sm text-gray-900">{formattedDates.updatedAt}</p>
              </div>
              {ticket.timeToResolution && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Time to Resolution</span>
                  <p className="text-sm text-gray-900">{ticket.timeToResolution}</p>
                </div>
              )}
            </div>
          </div>

          {/* User Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{ticket.userName}</p>
                  <p className="text-sm text-gray-500">{ticket.userEmail}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Technician Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assigned Technician</h3>
            {ticket.technicianName ? (
              <div className="flex items-center">
                <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-900">{ticket.technicianName}</p>
              </div>
            ) : (
              <div className="flex items-center">
                <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400 mr-2" />
                <p className="text-sm text-gray-500 italic">No technician assigned</p>
              </div>
            )}
          </div>

          {/* SLA Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SLA Information</h3>
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">SLA Target</span>
                <p className="text-sm text-gray-900">{formattedDates.slaTarget}</p>
              </div>
              {ticket.slaResolved && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Resolved At</span>
                  <p className="text-sm text-gray-900">{formattedDates.slaResolved}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">SLA Status</span>
                <div className="flex items-center mt-1">
                  {ticket.isSlaBreached ? (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  )}
                  <span className={`text-sm ${ticket.isSlaBreached ? 'text-red-600' : 'text-green-600'}`}>
                    {ticket.isSlaBreached ? 'SLA Breached' : 'SLA Met'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Linked Project */}
          {projectTrackerEnabled && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-blue-600" />
                  Project
                </h3>
              </div>
              {linkedProject ? (
                <div className="flex items-center justify-between">
                  <Link to={`/projects/${linkedProject.projectId}`}
                    className="text-sm text-blue-600 hover:underline font-medium flex items-center gap-1.5">
                    <ClipboardDocumentListIcon className="h-4 w-4" />
                    {linkedProject.projectName}
                  </Link>
                  <button onClick={handleUnlinkProject}
                    className="text-gray-400 hover:text-red-500 transition-colors" title="Unlink">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowLinkProject(true)}
                  className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
                  <LinkIcon className="h-4 w-4" /> Link to Project
                </button>
              )}

              {showLinkProject && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setShowLinkProject(false); setProjectSearch(''); setProjectSearchResults([]); }} />
                  <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                      <h3 className="text-base font-semibold text-gray-900">Link to Project</h3>
                      <button onClick={() => { setShowLinkProject(false); setProjectSearch(''); setProjectSearchResults([]); }}
                        className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="p-5 space-y-4">
                      <div className="flex gap-2">
                        <input
                          autoFocus
                          value={projectSearch}
                          onChange={e => setProjectSearch(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSearchProjects()}
                          placeholder="Search projects by name…"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button onClick={handleSearchProjects} disabled={searchingProjects || !projectSearch.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                          {searchingProjects ? '…' : 'Search'}
                        </button>
                      </div>
                      {projectSearchResults.length > 0 && (
                        <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto rounded-md border border-gray-200">
                          {projectSearchResults.map(p => (
                            <div key={p.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                              <span className="text-sm text-gray-900">{p.name}</span>
                              <button onClick={() => handleLinkProject(p.id)} disabled={linkingProject}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                                {linkingProject ? '…' : 'Link'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {projectSearch && !searchingProjects && projectSearchResults.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-2">No projects found.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetail; 